<template>
  <form-config-provider #default="{ formConfig }" :type="type" :fieldsMeta="fieldsMeta">
    <div class="entity-page">
      <entity-view
        v-if="formConfig"
        :type="type"
        :formConfig="formConfig"
        :desktop="desktop"
        :hasHistory="hasHistory"
        showBackButton
        @editJson="editJson"
        @editConfig="editConfig"
        @goBack="goBack"
        @goToHistory="goToHistory"
      >
        <template #form>
          <form-layout-provider
            #default="{ formLayout }"
            :formConfig="formConfig"
            :fieldsMeta="fieldsMeta"
          >
            <a-spin
              :spinning="!isRouteRefreshedAfterCreation && (!formLayout || !entityData)"
              class="edit-form-spinner"
            >
              <entity-form
                v-if="formLayout && entityData"
                :id="id"
                :type="type"
                :desktop="desktop"
                :formLayout="formLayout"
                :sourceData="entityData"
                :refsData="entityRefsData"
                :readOnly="isFormReadOnly"
                :oneColumn="false"
                :formConfig="formConfig"
                :descriptors="fieldsMeta"
                :canDeleteModel="canDeleteModel"
                :canCreateModel="canCreateModel"
                @close="goBack"
                @editEntity="editEntity"
                @duplicateEntity="duplicateEntity"
                @editingEntityIdChanged="editingEntityIdChanged"
              />
            </a-spin>
          </form-layout-provider>
        </template>
      </entity-view>

      <entity-form-json-modal
        v-model="isJsonEdit"
        :data="entityData"
        @dataUpdated="dataUpdated"
        @close="cancelJsonEdit"
      />

      <entity-form-config-modal
        v-if="formConfig"
        v-model="isConfigEdit"
        :config="formConfig"
        :descriptors="fieldsMeta"
        :typeName="type"
      />
    </div>
  </form-config-provider>
</template>

<script>
import store from '@/store';
import GET_QUERY from '@/queries/get';
import META_QUERY from '@/queries/meta';
import HISTORY_SHORT_QUERY from '@/queries/historyShort';
import FormConfigProvider from '@/components/edit-form/FormConfigProvider.vue';
import FormLayoutProvider from '@/components/edit-form/FormLayoutProvider.vue';
import EntityView from '@/components/edit-form/EntityView.vue';
import EntityForm from '@/components/edit-form/EntityForm.vue';
import EntityFormConfigModal from '@/components/edit-form/config/EntityFormConfigModal.vue';
import EntityFormJsonModal from '@/components/edit-form/EntityFormJsonModal.vue';

export default {
  name: 'TheEntityPage',
  components: {
    FormConfigProvider,
    FormLayoutProvider,
    EntityView,
    EntityForm,
    EntityFormConfigModal,
    EntityFormJsonModal,
  },

  props: {
    type: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    desktop: {
      type: Boolean,
      required: true,
    },
  },

  apollo: {
    meta: {
      ...META_QUERY,
      variables() {
        return {
          type: this.type,
        };
      },
      error(error) {
        this.emitError(`Ошибка получения meta-данных ${this.type}. ${error.message}`);
      },
    },
    hasHistory: {
      ...HISTORY_SHORT_QUERY,
      variables() {
        return {
          entityName: this.type,
          entityId: this.id,
        };
      },
      update: ({ history }) => !!history?.entries?.length,
      skip() {
        return this.isLocalDraft;
      },
      error(error) {
        this.emitError(
          `Ошибка получения истории сущности ${this.type}:${this.id}. ${error.message}`,
        );
      },
    },
    sourceData: {
      ...GET_QUERY,
      variables() {
        return {
          id: this.id,
          type: this.type,
        };
      },
      skip() {
        return this.isLocalDraft;
      },
      error(error, vm, key, type, { variables }) {
        this.emitError(`Ошибка получения данных сущности. ${variables.id} ${error.message};}`);
      },
    },
  },

  data() {
    return {
      browserHistoryDepth: 1000,
      isRouteRefreshedAfterCreation: false,
      isConfigEdit: false,
      isJsonEdit: false,
    };
  },

  computed: {
    user() {
      return store.state.user;
    },
    fieldsMeta() {
      return this.meta?.components[this.type]?.fields;
    },
    operations() {
      return this.extMeta?.operations || this.meta?.operations || {};
    },
    canCreateModel() {
      return this.operations.CREATE;
    },
    canUpdateModel() {
      return this.operations.UPDATE;
    },
    canDeleteModel() {
      return this.operations.DELETE;
    },

    isLocalDraft() {
      return this.id.startsWith('_temp');
    },

    isFormReadOnly() {
      return !this.isLocalDraft && !this.canUpdateModel;
    },

    entityData() {
      const data = this.isLocalDraft ? {} : this.sourceData?.document.data;
      return (
        data && {
          ...data,
          ...(store.mutate.getFormDraft(this.type, this.id)?.data || {}),
        }
      );
    },
    entityRefsData() {
      return this.isLocalDraft ? {} : this.sourceData?.dictionary;
    },
  },

  watch: {
    $route(to, from) {
      this.isRouteRefreshedAfterCreation =
        to.name === from.name &&
        to.params.type === from.params.type &&
        to.params.id &&
        from.params.id?.startsWith('_temp');
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === null) vm.browserHistoryDepth = 0;
      else vm.browserHistoryDepth++;
    });
  },

  methods: {
    goBack() {
      if (this.browserHistoryDepth > 0) {
        this.browserHistoryDepth--;
        this.$router.go(-1);
      } else {
        this.$router.push({
          name: 'TablePage',
          props: {
            type: this.type,
          },
        });
      }
    },

    editingEntityIdChanged(id) {
      this.$emit('editingEntityIdChanged', id);
    },

    editEntity(...args) {
      this.browserHistoryDepth++;
      this.$emit('editEntity', ...args);
    },

    duplicateEntity(type, id) {
      this.$emit(
        'editEntity',
        type,
        id,
        {
          operations: this.operations,
          fields: this.fieldsMeta,
        },
        true,
      );
    },

    dataUpdated(data) {
      store.mutate.storeFormDraft(this.type, this.id, data);
    },

    editConfig() {
      this.isConfigEdit = true;
    },

    editJson() {
      this.isJsonEdit = true;
    },

    cancelJsonEdit() {
      this.isJsonEdit = false;
    },

    goToHistory() {
      this.$router.push({
        name: 'History',
        params: {
          type: this.type,
          id: this.id,
        },
      });
    },
  },
};
</script>
